import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { formatTenantDateTime } from "@/lib/datetime";
import {
  Loader2, Facebook, Instagram, Share2, ShieldCheck, Plug, Unplug, AlertTriangle, TerminalSquare, Search, Twitter, AtSign, Globe, MessageSquare, Store, RefreshCw, Image, Home, ExternalLink, Linkedin, Music2,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const GOOGLE_ERROR_MESSAGES = {
  denied: "Cancelaste el permiso",
  expired_state: "Se venció el intento, probá de nuevo",
  no_refresh_token: "Google no devolvió el permiso permanente, volvé a conectar",
  exchange_failed: "No se pudo completar la conexión",
  unexpected: "Ocurrió un error inesperado conectando con Google. Volvé a intentar.",
};

const X_ERROR_MESSAGES = {
  denied: "Cancelaste el permiso",
  expired_state: "Se venció el intento, probá de nuevo",
  no_refresh_token: "X no devolvió el permiso permanente (offline.access), volvé a conectar",
  exchange_failed: "No se pudo completar la conexión con X",
  unexpected: "Ocurrió un error inesperado conectando con X. Volvé a intentar.",
};

const THREADS_ERROR_MESSAGES = {
  denied: "Cancelaste el permiso",
  expired_state: "Se venció el intento, probá de nuevo",
  exchange_failed: "No se pudo completar la conexión con Threads",
  unexpected: "Ocurrió un error inesperado conectando con Threads. Volvé a intentar.",
};

const PINTEREST_ERROR_MESSAGES = {
  denied: "Cancelaste el permiso",
  expired_state: "Se venció el intento, probá de nuevo",
  no_refresh_token: "Pinterest no devolvió el permiso de renovación, volvé a conectar",
  exchange_failed: "No se pudo completar la conexión con Pinterest",
  unexpected: "Ocurrió un error inesperado conectando con Pinterest. Volvé a intentar.",
};

const LINKEDIN_ERROR_MESSAGES = {
  denied: "Cancelaste el permiso",
  expired_state: "Se venció el intento, probá de nuevo",
  exchange_failed: "No se pudo completar la conexión con LinkedIn",
  no_organizations: "Tu cuenta de LinkedIn no administra ninguna página de empresa. Necesitás ser administrador de una Company Page para conectar.",
  unexpected: "Ocurrió un error inesperado conectando con LinkedIn. Volvé a intentar.",
};

const TIKTOK_ERROR_MESSAGES = {
  denied: "Cancelaste el permiso",
  expired_state: "Se venció el intento, probá de nuevo",
  no_refresh_token: "TikTok no devolvió el permiso de renovación, volvé a conectar",
  exchange_failed: "No se pudo completar la conexión con TikTok",
  unexpected: "Ocurrió un error inesperado conectando con TikTok. Volvé a intentar.",
};

const WORDPRESS_ERROR_MESSAGES = {
  rejected: "Cancelaste la autorización en WordPress",
  expired_state: "Se venció el intento, probá de nuevo",
  verify_failed: "Se guardó la conexión pero no pudimos verificarla -- revisá el motivo en la tarjeta de WordPress",
};

const PLATFORM = {
  facebook: { label: "Facebook", icon: Facebook, color: "#1877F2" },
  instagram: { label: "Instagram", icon: Instagram, color: "#E4405F" },
};

const STATUS = {
  connected: { label: "Conectada", cls: "bg-[#10B981]/10 text-[#10B981]" },
  disconnected: { label: "Desconectada", cls: "bg-[#EF4444]/10 text-[#EF4444]" },
  pending: { label: "Pendiente", cls: "bg-[#F59E0B]/10 text-[#F59E0B]" },
};

function maskSid(sid) {
  if (!sid || sid.length < 8) return sid || "";
  return `${sid.slice(0, 2)}••••${sid.slice(-4)}`;
}

const GBP_STATUS_BADGE = {
  ready: { label: "Ficha conectada", cls: "bg-[#10B981]/10 text-[#065F46]" },
  pending_approval: { label: "Esperando la aprobación de Google", cls: "bg-[#F59E0B]/10 text-[#92400E]" },
  google_not_connected: { label: "Falta conectar Google", cls: "bg-[#F59E0B]/10 text-[#92400E]" },
  unavailable: { label: "Pendiente de habilitar", cls: "bg-[#F4F4F5] text-[#71717A]" },
  error: { label: "Error al verificar", cls: "bg-[#EF4444]/10 text-[#991B1B]" },
};

function SimpleSocialCard({ testid, icon: Icon, color, label, description, account, connecting, onConnect, onDisconnect, children }) {
  return (
    <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid={testid}>
      {!account ? (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm grid place-items-center" style={{ backgroundColor: `${color}1a` }}>
              <Icon className="h-5 w-5" style={{ color }} />
            </div>
            <div>
              <div className="font-heading font-bold text-[#09090B]">{label}</div>
              <div className="text-xs text-[#71717A]">{description}</div>
            </div>
          </div>
          <Button
            onClick={onConnect} disabled={connecting} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
            data-testid={`${testid}-connect-button`}
          >
            {connecting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plug className="h-4 w-4 mr-1" />} Conectar
          </Button>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm grid place-items-center" style={{ backgroundColor: `${color}1a` }}>
                <Icon className="h-5 w-5" style={{ color }} />
              </div>
              <div>
                <div className="font-heading font-bold text-[#09090B]" data-testid={`${testid}-connected-name`}>{account.display_name}</div>
                <Badge className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${STATUS[account.connection_status]?.cls || STATUS.pending.cls}`}>
                  {STATUS[account.connection_status]?.label || account.connection_status}
                </Badge>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="text-[#EF4444]" onClick={onDisconnect} data-testid={`${testid}-disconnect-button`}>
              <Unplug className="h-3.5 w-3.5 mr-1" /> Desconectar
            </Button>
          </div>
          {children}
        </div>
      )}
    </Card>
  );
}

export default function ConnectionsPage() {
  const { activeTenantId, activeTenant, role } = useAuth();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [discovered, setDiscovered] = useState(null);
  const [selectedPages, setSelectedPages] = useState([]);
  const [diagnostics, setDiagnostics] = useState(null);
  const [googleConn, setGoogleConn] = useState(null);
  const [googleSites, setGoogleSites] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(true);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [googleConfigError, setGoogleConfigError] = useState(null);
  const [connectingX, setConnectingX] = useState(false);
  const [connectingThreads, setConnectingThreads] = useState(false);
  const [connectingPinterest, setConnectingPinterest] = useState(false);
  const [pinterestBoards, setPinterestBoards] = useState([]);
  const [pinterestLoadingBoards, setPinterestLoadingBoards] = useState(false);
  const [pinterestSelecting, setPinterestSelecting] = useState(false);
  const [pinterestNewBoardName, setPinterestNewBoardName] = useState("");
  const [pinterestCreatingBoard, setPinterestCreatingBoard] = useState(false);
  const [pinterestTrialMode, setPinterestTrialMode] = useState(true);
  const [connectingLinkedIn, setConnectingLinkedIn] = useState(false);
  const [linkedinOrganizations, setLinkedInOrganizations] = useState([]);
  const [linkedinLoadingOrgs, setLinkedInLoadingOrgs] = useState(false);
  const [linkedinSelecting, setLinkedInSelecting] = useState(false);
  const [linkedinTrialMode, setLinkedInTrialMode] = useState(true);
  const [rechecking, setRechecking] = useState(false);
  const [recheckCooldown, setRecheckCooldown] = useState(0);
  const [wpConnection, setWpConnection] = useState(null);
  const [wpLoading, setWpLoading] = useState(true);
  const [wpSiteUrl, setWpSiteUrl] = useState("");
  const [wpConnecting, setWpConnecting] = useState(false);
  const [twilioConn, setTwilioConn] = useState(null);
  const [twilioLoading, setTwilioLoading] = useState(true);
  const [twilioForm, setTwilioForm] = useState({ account_sid: "", api_key_sid: "", api_key_secret: "", messaging_service_sid: "" });
  const [twilioSaving, setTwilioSaving] = useState(false);
  const [twilioApproving, setTwilioApproving] = useState(false);
  const [gbpStatus, setGbpStatus] = useState(null);
  const [gbpLoading, setGbpLoading] = useState(true);
  const [gbpChecking, setGbpChecking] = useState(false);
  const [gbpLocations, setGbpLocations] = useState([]);
  const [gbpLoadingLocations, setGbpLoadingLocations] = useState(false);
  const [gbpSelecting, setGbpSelecting] = useState(false);
  const [nextdoorForm, setNextdoorForm] = useState({ display_name: "", page_url: "" });
  const [nextdoorSaving, setNextdoorSaving] = useState(false);
  const [connectingTikTok, setConnectingTikTok] = useState(false);
  const isConfigurator = role === "admin" || role === "manager";

  const loadTwilio = useCallback(async () => {
    if (!isConfigurator) { setTwilioLoading(false); return; }
    setTwilioLoading(true);
    try {
      const res = await api.get("/messaging/twilio/connection");
      setTwilioConn(res.data);
    } catch {
      setTwilioConn(null);
    } finally {
      setTwilioLoading(false);
    }
  }, [isConfigurator]);

  useEffect(() => { if (activeTenantId) loadTwilio(); }, [activeTenantId, loadTwilio]);

  const saveTwilio = async () => {
    setTwilioSaving(true);
    try {
      await api.put("/messaging/twilio/connection", twilioForm);
      toast.success("Credenciales de Twilio guardadas");
      setTwilioForm({ account_sid: "", api_key_sid: "", api_key_secret: "", messaging_service_sid: "" });
      loadTwilio();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron guardar las credenciales");
    } finally {
      setTwilioSaving(false);
    }
  };

  const approveTwilio = async () => {
    setTwilioApproving(true);
    try {
      await api.post("/messaging/twilio/connection/approve");
      toast.success("Registro A2P marcado como aprobado");
      loadTwilio();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aprobar el registro");
    } finally {
      setTwilioApproving(false);
    }
  };

  const loadGbpLocations = useCallback(async () => {
    setGbpLoadingLocations(true);
    try {
      const res = await api.get("/google/business/locations");
      setGbpLocations(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron traer las fichas de Business Profile");
    } finally {
      setGbpLoadingLocations(false);
    }
  }, []);

  const loadGbp = useCallback(async () => {
    if (!isConfigurator) { setGbpLoading(false); return; }
    setGbpLoading(true);
    try {
      const res = await api.get("/google/business/status");
      setGbpStatus(res.data);
      if (res.data?.gbp_access_status === "ready") loadGbpLocations();
    } catch {
      setGbpStatus(null);
    } finally {
      setGbpLoading(false);
    }
  }, [isConfigurator, loadGbpLocations]);

  useEffect(() => { if (activeTenantId) loadGbp(); }, [activeTenantId, loadGbp]);

  const checkGbpAccess = async () => {
    setGbpChecking(true);
    try {
      const res = await api.post("/google/business/check-access");
      setGbpStatus(res.data);
      if (res.data.gbp_access_status === "ready") loadGbpLocations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo verificar el acceso");
    } finally {
      setGbpChecking(false);
    }
  };

  const selectGbpLocation = async (locationName) => {
    const loc = gbpLocations.find((l) => l.location_name === locationName);
    if (!loc) return;
    setGbpSelecting(true);
    try {
      const res = await api.post("/google/business/select-location", {
        account_name: loc.account_name, location_name: loc.location_name, location_title: loc.location_title,
      });
      setGbpStatus(res.data);
      toast.success("Ficha de Business Profile seleccionada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo elegir la ficha");
    } finally {
      setGbpSelecting(false);
    }
  };

  const loadWordPress = useCallback(async () => {
    setWpLoading(true);
    try {
      const res = await api.get("/integrations/wordpress/connection");
      setWpConnection(res.data);
    } catch {
      setWpConnection(null);
    } finally {
      setWpLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTenantId) loadWordPress(); }, [activeTenantId, loadWordPress]);

  const startWordPressConnect = async () => {
    if (!wpSiteUrl.trim()) return;
    setWpConnecting(true);
    try {
      const res = await api.get("/integrations/wordpress/start", { params: { site_url: wpSiteUrl.trim() } });
      window.location.href = res.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo descubrir la API de WordPress en esa URL");
      setWpConnecting(false);
    }
  };

  const disconnectWordPress = async () => {
    try {
      await api.post("/integrations/wordpress/disconnect");
      toast.success("WordPress desconectado");
      loadWordPress();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo desconectar");
    }
  };

  const loadGoogle = useCallback(async () => {
    setGoogleLoading(true);
    try {
      const res = await api.get("/google/status");
      setGoogleConn(res.data);
      if (res.data?.connection_status === "connected") {
        try {
          const sitesRes = await api.get("/google/sites");
          setGoogleSites(sitesRes.data);
        } catch {
          setGoogleSites([]);
        }
      }
    } finally {
      setGoogleLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/social/accounts");
      setAccounts(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTenantId) load(); }, [activeTenantId, load]);
  useEffect(() => { if (activeTenantId) loadGoogle(); }, [activeTenantId, loadGoogle]);

  const loadDiagnostics = useCallback(() => {
    if (role === "admin" && activeTenantId) {
      api.get("/integrations/diagnostics").then((r) => {
        setDiagnostics(r.data);
        setRecheckCooldown(r.data.site_health_recheck_retry_after_seconds || 0);
      }).catch(() => {});
    }
  }, [role, activeTenantId]);

  useEffect(() => { loadDiagnostics(); }, [loadDiagnostics]);

  useEffect(() => {
    if (recheckCooldown <= 0) return;
    const timer = setInterval(() => setRecheckCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [recheckCooldown]);

  const handleRecheckSite = async () => {
    setRechecking(true);
    try {
      await api.post("/integrations/site-health/recheck");
      toast.success("Sitio re-verificado");
      loadDiagnostics();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = (detail && typeof detail === "object") ? detail.message : detail;
      toast.error(message || "No se pudo re-verificar el sitio");
      if (detail && typeof detail === "object" && detail.retry_after_seconds) {
        setRecheckCooldown(detail.retry_after_seconds);
      }
    } finally {
      setRechecking(false);
    }
  };

  useEffect(() => {
    const sessionCode = params.get("discovered");
    const metaError = params.get("meta_error");
    const googleError = params.get("google_error");
    const xError = params.get("x_error");
    const threadsError = params.get("threads_error");
    const pinterestError = params.get("pinterest_error");
    const linkedinError = params.get("linkedin_error");
    const tiktokError = params.get("tiktok_error");
    const wpError = params.get("wordpress_error");
    const wpConnected = params.get("wordpress_connected");
    if (metaError) {
      const message = metaError === "unexpected"
        ? "Ocurrió un error inesperado conectando con Meta. Volvé a intentar."
        : "No se pudo conectar con Meta. Volvé a intentar.";
      toast.error(message);
      setParams({});
    }
    if (googleError) {
      toast.error(GOOGLE_ERROR_MESSAGES[googleError] || "No se pudo conectar con Google. Volvé a intentar.");
      setParams({});
    }
    if (xError) {
      toast.error(X_ERROR_MESSAGES[xError] || "No se pudo conectar con X. Volvé a intentar.");
      setParams({});
    }
    if (threadsError) {
      toast.error(THREADS_ERROR_MESSAGES[threadsError] || "No se pudo conectar con Threads. Volvé a intentar.");
      setParams({});
    }
    if (pinterestError) {
      toast.error(PINTEREST_ERROR_MESSAGES[pinterestError] || "No se pudo conectar con Pinterest. Volvé a intentar.");
      setParams({});
    }
    if (linkedinError) {
      toast.error(LINKEDIN_ERROR_MESSAGES[linkedinError] || "No se pudo conectar con LinkedIn. Volvé a intentar.");
      setParams({});
    }
    if (tiktokError) {
      toast.error(TIKTOK_ERROR_MESSAGES[tiktokError] || "No se pudo conectar con TikTok. Volvé a intentar.");
      setParams({});
    }
    if (wpError) {
      toast.error(WORDPRESS_ERROR_MESSAGES[wpError] || "No se pudo conectar con WordPress. Volvé a intentar.");
      setParams({});
    }
    if (wpConnected) {
      toast.success("WordPress conectado");
      loadWordPress();
      setParams({});
    }
    if (sessionCode) {
      api.get(`/integrations/meta/discovered/${sessionCode}`)
        .then((r) => { setDiscovered(sessionCode); setSelectedPages(r.data.pages); })
        .catch(() => toast.error("La sesión de conexión venció, volvé a intentar"))
        .finally(() => setParams({}));
    }
  }, [params, setParams, loadWordPress]);

  const startConnect = async () => {
    setConnecting(true);
    try {
      const res = await api.get("/integrations/meta/start");
      window.location.assign(res.data.authorization_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la conexión con Meta");
      setConnecting(false);
    }
  };

  const togglePage = (pageId) => {
    setSelectedPages((prev) => prev.map((p) => (p.page_id === pageId ? { ...p, _checked: !p._checked } : p)));
  };

  const confirmConnect = async () => {
    const pageIds = selectedPages.filter((p) => p._checked).map((p) => p.page_id);
    if (pageIds.length === 0) {
      toast.error("Elegí al menos una página");
      return;
    }
    try {
      await api.post("/integrations/meta/connect", { session_code: discovered, page_ids: pageIds });
      toast.success("Cuentas conectadas");
      setDiscovered(null);
      setSelectedPages([]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo conectar");
    }
  };

  const disconnect = async (account) => {
    try {
      await api.post(`/social/accounts/${account.id}/disconnect`);
      toast.success(`${account.display_name} desconectada`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo desconectar");
    }
  };

  const startXConnect = async () => {
    setConnectingX(true);
    try {
      const res = await api.get("/integrations/x/start");
      window.location.assign(res.data.authorization_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la conexión con X");
      setConnectingX(false);
    }
  };

  const startThreadsConnect = async () => {
    setConnectingThreads(true);
    try {
      const res = await api.get("/integrations/threads/start");
      window.location.assign(res.data.authorization_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la conexión con Threads");
      setConnectingThreads(false);
    }
  };

  const startPinterestConnect = async () => {
    setConnectingPinterest(true);
    try {
      const res = await api.get("/integrations/pinterest/start");
      window.location.assign(res.data.authorization_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la conexión con Pinterest");
      setConnectingPinterest(false);
    }
  };

  const loadPinterestBoards = useCallback(async () => {
    setPinterestLoadingBoards(true);
    try {
      const res = await api.get("/integrations/pinterest/boards");
      setPinterestBoards(res.data);
    } catch {
      setPinterestBoards([]);
    } finally {
      setPinterestLoadingBoards(false);
    }
  }, []);

  const pinterestAccount = accounts.find((a) => a.platform === "pinterest") || null;

  useEffect(() => {
    if (pinterestAccount) {
      loadPinterestBoards();
      api.get("/integrations/pinterest/trial-status").then((r) => setPinterestTrialMode(r.data.trial_mode)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinterestAccount?.id, loadPinterestBoards]);

  const selectPinterestBoard = async (boardId) => {
    const board = pinterestBoards.find((b) => b.id === boardId);
    if (!board) return;
    setPinterestSelecting(true);
    try {
      await api.post("/integrations/pinterest/select-board", { board_id: board.id, board_name: board.name });
      toast.success("Tablero de Pinterest elegido");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo elegir el tablero");
    } finally {
      setPinterestSelecting(false);
    }
  };

  const createPinterestBoard = async () => {
    if (!pinterestNewBoardName.trim()) return;
    setPinterestCreatingBoard(true);
    try {
      await api.post("/integrations/pinterest/boards", { name: pinterestNewBoardName.trim() });
      toast.success("Tablero creado y elegido");
      setPinterestNewBoardName("");
      loadPinterestBoards();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el tablero");
    } finally {
      setPinterestCreatingBoard(false);
    }
  };

  const startLinkedInConnect = async () => {
    setConnectingLinkedIn(true);
    try {
      const res = await api.get("/integrations/linkedin/start");
      window.location.assign(res.data.authorization_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la conexión con LinkedIn");
      setConnectingLinkedIn(false);
    }
  };

  const startTikTokConnect = async () => {
    setConnectingTikTok(true);
    try {
      const res = await api.get("/integrations/tiktok/start");
      window.location.assign(res.data.authorization_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la conexión con TikTok");
      setConnectingTikTok(false);
    }
  };

  const loadLinkedInOrganizations = useCallback(async () => {
    setLinkedInLoadingOrgs(true);
    try {
      const res = await api.get("/integrations/linkedin/organizations");
      setLinkedInOrganizations(res.data);
    } catch {
      setLinkedInOrganizations([]);
    } finally {
      setLinkedInLoadingOrgs(false);
    }
  }, []);

  const linkedinAccount = accounts.find((a) => a.platform === "linkedin") || null;

  useEffect(() => {
    if (linkedinAccount) {
      loadLinkedInOrganizations();
      api.get("/integrations/linkedin/trial-status").then((r) => setLinkedInTrialMode(r.data.trial_mode)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedinAccount?.id, loadLinkedInOrganizations]);

  const selectLinkedInOrganization = async (organizationId) => {
    const org = linkedinOrganizations.find((o) => o.organization_id === organizationId);
    if (!org) return;
    setLinkedInSelecting(true);
    try {
      await api.post("/integrations/linkedin/select-organization", { organization_id: org.organization_id, organization_name: org.organization_name });
      toast.success("Página de empresa de LinkedIn elegida");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo elegir la página");
    } finally {
      setLinkedInSelecting(false);
    }
  };

  const startGoogleConnect = async () => {
    setConnectingGoogle(true);
    setGoogleConfigError(null);
    try {
      const res = await api.get("/integrations/google/start");
      window.location.assign(res.data.authorization_url);
    } catch (e) {
      const detail = e?.response?.data?.detail || "No se pudo iniciar la conexión con Google";
      if (e?.response?.status === 503) {
        // Google no está configurado todavía: mostrar el motivo en pantalla,
        // nunca redirigir a la pantalla cruda de error de Google.
        setGoogleConfigError(detail);
      } else {
        toast.error(detail);
      }
      setConnectingGoogle(false);
    }
  };

  const selectGoogleSite = async (siteUrl) => {
    try {
      const res = await api.post("/google/select-site", { site_url: siteUrl });
      setGoogleConn(res.data);
      toast.success("Sitio de Search Console seleccionado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo elegir el sitio");
    }
  };

  const disconnectGoogle = async () => {
    try {
      await api.delete("/google/connection");
      toast.success("Google desconectado");
      setGoogleConn(null);
      setGoogleSites([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo desconectar Google");
    }
  };

  const metaAccounts = accounts.filter((a) => a.platform === "facebook" || a.platform === "instagram");
  const xAccount = accounts.find((a) => a.platform === "twitter") || null;
  const threadsAccount = accounts.find((a) => a.platform === "threads") || null;
  const nextdoorAccount = accounts.find((a) => a.platform === "nextdoor") || null;
  const tiktokAccount = accounts.find((a) => a.platform === "tiktok") || null;

  useEffect(() => {
    if (nextdoorAccount) {
      setNextdoorForm({ display_name: nextdoorAccount.display_name, page_url: nextdoorAccount.nextdoor_page_url || "" });
    }
  }, [nextdoorAccount?.id]);

  const activateNextdoor = async () => {
    if (!nextdoorForm.display_name.trim() || !nextdoorForm.page_url.trim()) return;
    setNextdoorSaving(true);
    try {
      await api.post("/integrations/nextdoor/activate", nextdoorForm);
      toast.success("Nextdoor activado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo activar Nextdoor");
    } finally {
      setNextdoorSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="connections-page">
      <div className="flex items-start gap-2 text-xs text-[#71717A] bg-white border border-[#E4E4E7] rounded-sm p-3">
        <ShieldCheck className="h-4 w-4 text-[#0055FF] shrink-0 mt-0.5" />
        Las credenciales de cada cuenta se guardan cifradas y nunca se exponen por la API. Vos nunca ves ni pegás un token: la
        conexión se hace 100% por OAuth de Meta.
      </div>

      {diagnostics && (
        <Card data-testid="diagnostics-panel" className="rounded-sm border-[#E4E4E7] shadow-none p-4 bg-[#09090B] text-white">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#A1A1AA]">
            <TerminalSquare className="h-3.5 w-3.5" /> Diagnóstico (solo admin)
          </div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-mono">
            <div><span className="text-[#71717A]">PUBLIC_BASE_URL:</span> {diagnostics.public_base_url}</div>
            <div><span className="text-[#71717A]">redirect_uri Meta:</span> {diagnostics.meta_redirect_uri}</div>
            <div><span className="text-[#71717A]">ALLOW_PUBLISH:</span> {String(diagnostics.allow_publish)}</div>
            <div><span className="text-[#71717A]">App de Meta configurada:</span> {String(diagnostics.meta_app_configured)}</div>
            <div><span className="text-[#71717A]">App de TikTok configurada:</span> {String(diagnostics.tiktok_app_configured)}</div>
            <div><span className="text-[#71717A]">Object storage:</span> {String(diagnostics.object_storage_ready)}</div>
            <div><span className="text-[#71717A]">Límite diario default:</span> {diagnostics.default_daily_publish_limit}</div>
            <div><span className="text-[#71717A]">Scheduler interno (asyncio):</span> {String(diagnostics.internal_scheduler_enabled)}</div>
            <div data-testid="diagnostics-openai-key"><span className="text-[#71717A]">OpenAI key:</span> {diagnostics.openai_key_configured ? "configurada" : "ausente"}</div>
            <div data-testid="diagnostics-embeddings"><span className="text-[#71717A]">Embeddings:</span> {diagnostics.embeddings_active ? `activos (${diagnostics.embedding_model})` : "apagados"}</div>
            <div data-testid="diagnostics-last-scheduler-tick">
              <span className="text-[#71717A]">Último tick del publicador:</span>{" "}
              {diagnostics.last_scheduler_tick_at
                ? `${formatTenantDateTime(diagnostics.last_scheduler_tick_at, activeTenant?.timezone)} (${diagnostics.last_scheduler_tick_source === "internal" ? "loop interno" : "cron externo"})`
                : "nunca corrió"}
            </div>
            <div data-testid="diagnostics-last-health-check">
              <span className="text-[#71717A]">Último health check:</span>{" "}
              {diagnostics.last_health_check_tick_at ? formatTenantDateTime(diagnostics.last_health_check_tick_at, activeTenant?.timezone) : "nunca corrió"}
            </div>
            <div data-testid="diagnostics-autopilot">
              <span className="text-[#71717A]">Piloto automático:</span>{" "}
              {diagnostics.autopilot_enabled ? `activo (próxima: ${diagnostics.autopilot_next_run_label})` : "apagado"}
            </div>
            <div data-testid="diagnostics-client-site">
              <span className="text-[#71717A]">Sitio del cliente:</span>{" "}
              {diagnostics.client_site_status === "ok" && <span className="text-[#10B981]">OK</span>}
              {diagnostics.client_site_status === "down" && (
                <span className="text-[#EF4444]" data-testid="site-status-down">
                  Caído — error del servidor (HTTP {diagnostics.client_site_status_code || "sin respuesta"})
                </span>
              )}
              {diagnostics.client_site_status === "blocked" && (
                <span className="text-[#F59E0B]" data-testid="site-status-blocked">
                  No se pudo comprobar (bloqueado, HTTP {diagnostics.client_site_status_code})
                </span>
              )}
              {diagnostics.client_site_status === "unreachable" && (
                <span className="text-[#EF4444]" data-testid="site-status-unreachable">
                  No responde (timeout o sin conexión)
                </span>
              )}
              {diagnostics.client_site_status === "unknown" && <span className="text-[#71717A]">todavía sin chequear</span>}
            </div>
            {diagnostics.client_site_status && diagnostics.client_site_status !== "unknown" && (
              <>
                <div data-testid="diagnostics-client-site-checked-at">
                  <span className="text-[#71717A]">Último chequeo:</span>{" "}
                  {diagnostics.client_site_last_checked_at
                    ? `${formatTenantDateTime(diagnostics.client_site_last_checked_at, activeTenant?.timezone)} (${diagnostics.client_site_latency_ms ?? "?"}ms)`
                    : "nunca"}
                </div>
                {diagnostics.client_site_status !== "ok" && (
                  <div data-testid="diagnostics-client-site-failed-since">
                    <span className="text-[#71717A]">Fallando desde:</span>{" "}
                    {diagnostics.client_site_failed_since
                      ? `${formatTenantDateTime(diagnostics.client_site_failed_since, activeTenant?.timezone)} (${diagnostics.client_site_consecutive_failures} chequeo(s) consecutivo(s))`
                      : "—"}
                  </div>
                )}
              </>
            )}
            <div>
              <Button
                variant="outline" size="sm" onClick={handleRecheckSite} disabled={rechecking || recheckCooldown > 0}
                className="rounded-md h-7 text-xs border-[#3F3F46] bg-transparent text-white hover:bg-[#18181B] hover:text-white"
                data-testid="site-health-recheck-button"
              >
                {rechecking ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                {recheckCooldown > 0 ? `Esperá ${recheckCooldown}s` : "Re-verificar ahora"}
              </Button>
            </div>
          </div>
          {diagnostics.site_health_stale && (
            <div className="mt-2 flex items-center gap-2 text-[11px] text-[#F59E0B] font-medium" data-testid="site-health-stale-warning">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {diagnostics.site_health_stale_message}
            </div>
          )}
          {diagnostics.scheduler_stale && (
            <div className="mt-3 flex items-center gap-2 text-[11px] text-[#EF4444] font-medium" data-testid="scheduler-stale-warning">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {diagnostics.scheduler_stale_message}
            </div>
          )}
          {diagnostics.overdue_scheduled_count > 0 && (
            <div className="mt-2 flex items-center gap-2 text-[11px] text-[#EF4444]" data-testid="overdue-publications-warning">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {diagnostics.overdue_scheduled_count} publicación(es) programada(s) con más de 10 minutos de retraso.
            </div>
          )}
          {diagnostics.is_temporary_domain && (
            <div className="mt-3 flex items-center gap-2 text-[11px] text-[#F59E0B]" data-testid="temporary-domain-warning">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Dominio temporal de la plataforma: la publicación real está bloqueada hasta poner ALLOW_PUBLISH=true con el dominio final del cliente.
            </div>
          )}
        </Card>
      )}

      {discovered && (
        <Card data-testid="discovered-pages-card" className="rounded-sm border-[#0055FF] shadow-none p-6">
          <div className="font-heading font-bold text-[#09090B]">Elegí qué páginas conectar</div>
          <div className="mt-4 space-y-3">
            {selectedPages.map((p) => (
              <label key={p.page_id} className="flex items-center gap-3 p-3 border border-[#E4E4E7] rounded-sm cursor-pointer" data-testid={`discovered-page-${p.page_id}`}>
                <Checkbox checked={!!p._checked} onCheckedChange={() => togglePage(p.page_id)} data-testid={`discovered-page-checkbox-${p.page_id}`} />
                <div className="flex-1">
                  <div className="text-sm font-medium">{p.page_name}</div>
                  <div className="text-xs text-[#71717A]">
                    {p.has_instagram ? "Incluye cuenta de Instagram vinculada" : "Sin Instagram vinculado"}
                    {p.token_expiry_status === "expires_soon" && (
                      <span className="text-[#F59E0B]"> · el token va a expirar, vas a tener que reconectar</span>
                    )}
                    {p.token_expiry_status === "unknown" && (
                      <span className="text-[#71717A]"> · no pudimos verificar la vigencia del token</span>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <Button data-testid="confirm-connect-pages-button" onClick={confirmConnect} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md">Conectar seleccionadas</Button>
            <Button variant="ghost" onClick={() => { setDiscovered(null); setSelectedPages([]); }}>Cancelar</Button>
          </div>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-[#71717A]">Cuentas de Facebook e Instagram conectadas a este tenant.</p>
        <Button data-testid="connect-meta-button" onClick={startConnect} disabled={connecting} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md">
          {connecting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plug className="h-4 w-4 mr-1" />} Conectar Facebook / Instagram
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>
      ) : metaAccounts.length === 0 ? (
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-10 text-center" data-testid="social-empty">
          <Share2 className="h-8 w-8 text-[#A1A1AA] mx-auto" />
          <p className="mt-3 text-sm text-[#71717A]">No hay cuentas sociales conectadas.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {metaAccounts.map((a) => {
            const p = PLATFORM[a.platform] || { label: a.platform, icon: Share2, color: "#71717A" };
            const s = STATUS[a.connection_status] || STATUS.pending;
            const Icon = p.icon;
            return (
              <Card key={a.id} className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid={`social-account-${a.id}`}>
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-sm grid place-items-center" style={{ backgroundColor: `${p.color}1a` }}>
                    <Icon className="h-5 w-5" style={{ color: p.color }} />
                  </div>
                  <Badge className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${s.cls}`}>{s.label}</Badge>
                </div>
                <div className="mt-4 font-heading font-bold text-[#09090B]">{a.display_name}</div>
                <div className="text-xs text-[#71717A] mt-1">{p.label} · {a.external_id}</div>
                <div className="text-xs text-[#71717A] mt-1">
                  Última verificación: {a.last_verified_at ? formatTenantDateTime(a.last_verified_at, activeTenant?.timezone) : "—"}
                </div>
                <div className="text-xs text-[#71717A]">Límite: {a.daily_publish_limit} publicaciones/día</div>
                <div className="text-xs mt-1" data-testid={`token-expiry-${a.id}`}>
                  {a.token_expiry_status === "no_expiry" && <span className="text-[#71717A]">Token permanente</span>}
                  {a.token_expiry_status === "expires_soon" && (
                    <span className="text-[#F59E0B] font-medium">
                      Vence el {formatTenantDateTime(a.token_expires_at, activeTenant?.timezone)}
                    </span>
                  )}
                  {a.token_expiry_status === "unknown" && (
                    <span className="text-[#F59E0B] font-medium">No pudimos verificar el token</span>
                  )}
                </div>
                {a.connection_status === "connected" && (
                  <Button variant="ghost" size="sm" className="mt-3 text-[#EF4444] px-0" onClick={() => disconnect(a)} data-testid={`disconnect-${a.id}`}>
                    <Unplug className="h-3.5 w-3.5 mr-1" /> Desconectar
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SimpleSocialCard
          testid="x-connection-card" icon={Twitter} color="#000000" label="X"
          description="Publicá en X automáticamente." account={xAccount} connecting={connectingX}
          onConnect={startXConnect} onDisconnect={() => disconnect(xAccount)}
        />
        <SimpleSocialCard
          testid="threads-connection-card" icon={AtSign} color="#000000" label="Threads"
          description="Publicá en Threads automáticamente." account={threadsAccount} connecting={connectingThreads}
          onConnect={startThreadsConnect} onDisconnect={() => disconnect(threadsAccount)}
        />
      </div>

      <div>
        <SimpleSocialCard
          testid="pinterest-connection-card" icon={Image} color="#E60023" label="Pinterest"
          description="Publicá pines con la imagen del Estudio Creativo y el link de tracking." account={pinterestAccount}
          connecting={connectingPinterest} onConnect={startPinterestConnect} onDisconnect={() => disconnect(pinterestAccount)}
        >
          <div className="mt-4 space-y-3">
            {pinterestTrialMode && (
              <p className="text-xs text-[#92400E] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-sm p-3" data-testid="pinterest-trial-notice">
                Pinterest está en modo de prueba: los pines que publiques solo los vas a ver vos. Estamos tramitando el acceso completo.
              </p>
            )}
            {pinterestLoadingBoards ? (
              <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-[#0055FF]" /></div>
            ) : pinterestBoards.length > 0 ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#71717A]">Tablero:</span>
                <Select value={pinterestAccount?.pinterest_board_id || undefined} onValueChange={selectPinterestBoard} disabled={pinterestSelecting}>
                  <SelectTrigger data-testid="pinterest-board-select" className="h-8 w-64 text-xs">
                    <SelectValue placeholder="Elegí un tablero" />
                  </SelectTrigger>
                  <SelectContent>
                    {pinterestBoards.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#71717A]">No hay ningún tablero todavía --</span>
                <Input
                  value={pinterestNewBoardName} onChange={(e) => setPinterestNewBoardName(e.target.value)}
                  placeholder="Nombre del tablero" className="h-8 w-48 text-xs" data-testid="pinterest-new-board-input"
                />
                <Button size="sm" onClick={createPinterestBoard} disabled={pinterestCreatingBoard || !pinterestNewBoardName.trim()} data-testid="pinterest-create-board-button">
                  {pinterestCreatingBoard ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null} Crear
                </Button>
              </div>
            )}
            {pinterestAccount?.token_expiry_status === "continuous_refresh" && pinterestAccount?.token_expires_at && (
              <p className="text-[10px] text-[#A1A1AA]" data-testid="pinterest-renewal-notice">
                La conexión se renueva sola; vence el {formatTenantDateTime(pinterestAccount.token_expires_at, activeTenant?.timezone)} si falla.
              </p>
            )}
          </div>
        </SimpleSocialCard>
      </div>

      <div>
        <SimpleSocialCard
          testid="linkedin-connection-card" icon={Linkedin} color="#0A66C2" label="LinkedIn"
          description="Necesitás una página de empresa de LinkedIn y ser administrador de ella (no funciona con un perfil personal). Publicá en nombre de la organización con imagen y link de tracking."
          account={linkedinAccount} connecting={connectingLinkedIn} onConnect={startLinkedInConnect} onDisconnect={() => disconnect(linkedinAccount)}
        >
          <div className="mt-4 space-y-3">
            {linkedinTrialMode && (
              <p className="text-xs text-[#92400E] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-sm p-3" data-testid="linkedin-trial-notice">
                LinkedIn está en acceso de desarrollo. Las publicaciones salen normalmente; estamos tramitando el acceso completo.
              </p>
            )}
            {linkedinLoadingOrgs ? (
              <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-[#0055FF]" /></div>
            ) : linkedinOrganizations.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#71717A]">Página de empresa:</span>
                <Select value={linkedinAccount?.linkedin_organization_id || undefined} onValueChange={selectLinkedInOrganization} disabled={linkedinSelecting}>
                  <SelectTrigger data-testid="linkedin-organization-select" className="h-8 w-64 text-xs">
                    <SelectValue placeholder="Elegí una página" />
                  </SelectTrigger>
                  <SelectContent>
                    {linkedinOrganizations.map((o) => (
                      <SelectItem key={o.organization_id} value={o.organization_id}>{o.organization_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {linkedinAccount?.token_expiry_status === "continuous_refresh" && linkedinAccount?.token_expires_at && (
              <p className="text-[10px] text-[#A1A1AA]" data-testid="linkedin-renewal-notice">
                La conexión se renueva sola (partner de renovación aprobado); vence el {formatTenantDateTime(linkedinAccount.token_expires_at, activeTenant?.timezone)} si falla.
              </p>
            )}
            {linkedinAccount?.token_expiry_status === "manual_reauth_required" && linkedinAccount?.token_expires_at && (
              <p className="text-xs text-[#92400E] bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-sm p-3" data-testid="linkedin-manual-reauth-notice">
                LinkedIn no otorgó renovación automática para esta app -- vence el {formatTenantDateTime(linkedinAccount.token_expires_at, activeTenant?.timezone)} y hay que volver a autorizarla a mano antes de esa fecha.
              </p>
            )}
          </div>
        </SimpleSocialCard>
      </div>

      <div>
        <p className="text-sm text-[#71717A] mb-3">
          Nextdoor no tiene API de publicación: es una red asistida — te deja el post listo con el link de
          seguimiento y lo publicás vos con un toque.
        </p>
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="nextdoor-connection-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm grid place-items-center bg-[#8ED500]/15 shrink-0">
                <Home className="h-5 w-5 text-[#4D7A00]" />
              </div>
              <div>
                <div className="font-heading font-bold text-[#09090B] flex items-center gap-2">
                  Nextdoor
                  <span
                    className="text-[10px] uppercase tracking-wider bg-[#8ED500]/15 text-[#4D7A00] px-1.5 py-0.5 rounded-sm cursor-help"
                    title="Nextdoor no permite que otras herramientas publiquen automáticamente. La plataforma te deja el post listo y vos lo publicás con un toque."
                    data-testid="nextdoor-assisted-badge"
                  >
                    Asistida
                  </span>
                </div>
                <div className="text-xs text-[#71717A]">
                  {nextdoorAccount ? nextdoorAccount.display_name : "Todavía no activada"}
                </div>
              </div>
            </div>
            {nextdoorAccount && (
              <Badge className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${STATUS[nextdoorAccount.connection_status]?.cls || STATUS.pending.cls}`}>
                {STATUS[nextdoorAccount.connection_status]?.label || nextdoorAccount.connection_status}
              </Badge>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input
              value={nextdoorForm.display_name} onChange={(e) => setNextdoorForm((f) => ({ ...f, display_name: e.target.value }))}
              placeholder="Nombre visible (ej. tu negocio)" className="text-xs" data-testid="nextdoor-display-name-input"
            />
            <Input
              value={nextdoorForm.page_url} onChange={(e) => setNextdoorForm((f) => ({ ...f, page_url: e.target.value }))}
              placeholder="URL de tu página de negocio en Nextdoor" className="text-xs" data-testid="nextdoor-page-url-input"
            />
          </div>
          <p className="text-[10px] text-[#A1A1AA] mt-1">
            Necesitás una página de negocio YA reclamada en Nextdoor (el Share Plugin no funciona con páginas nuevas sin reclamar).
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm" onClick={activateNextdoor} disabled={nextdoorSaving || !nextdoorForm.display_name.trim() || !nextdoorForm.page_url.trim()}
              className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" data-testid="nextdoor-activate-button"
            >
              {nextdoorSaving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plug className="h-3.5 w-3.5 mr-1" />}
              {nextdoorAccount ? "Guardar" : "Activar"}
            </Button>
            {nextdoorAccount?.nextdoor_page_url && (
              <Button asChild size="sm" variant="outline" className="rounded-md" data-testid="nextdoor-open-page-button">
                <a href={nextdoorAccount.nextdoor_page_url} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Abrir mi página
                </a>
              </Button>
            )}
            {nextdoorAccount?.connection_status === "connected" && (
              <Button variant="ghost" size="sm" className="text-[#EF4444]" onClick={() => disconnect(nextdoorAccount)} data-testid="nextdoor-disconnect-button">
                <Unplug className="h-3.5 w-3.5 mr-1" /> Desconectar
              </Button>
            )}
          </div>
        </Card>
      </div>

      <div>
        <p className="text-sm text-[#71717A] mb-3">
          TikTok solo deja que otras herramientas dejen el post listo. Te llega una notificación a la app y lo publicás vos con un toque.
        </p>
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="tiktok-connection-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-sm grid place-items-center bg-[#FE2C55]/15 shrink-0">
                <Music2 className="h-5 w-5 text-[#FE2C55]" />
              </div>
              <div>
                <div className="font-heading font-bold text-[#09090B] flex items-center gap-2">
                  TikTok
                  <span
                    className="text-[10px] uppercase tracking-wider bg-[#FE2C55]/15 text-[#B91C4C] px-1.5 py-0.5 rounded-sm cursor-help"
                    title="TikTok solo deja que otras herramientas dejen el post listo. Te llega una notificación a la app y lo publicás vos con un toque."
                    data-testid="tiktok-assisted-badge"
                  >
                    Asistida
                  </span>
                </div>
                <div className="text-xs text-[#71717A]">
                  {tiktokAccount ? tiktokAccount.display_name : "Todavía no conectada"}
                </div>
              </div>
            </div>
            {tiktokAccount ? (
              <div className="flex items-center gap-2">
                <Badge className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${STATUS[tiktokAccount.connection_status]?.cls || STATUS.pending.cls}`}>
                  {STATUS[tiktokAccount.connection_status]?.label || tiktokAccount.connection_status}
                </Badge>
                <Button variant="ghost" size="sm" className="text-[#EF4444]" onClick={() => disconnect(tiktokAccount)} data-testid="tiktok-disconnect-button">
                  <Unplug className="h-3.5 w-3.5 mr-1" /> Desconectar
                </Button>
              </div>
            ) : (
              <Button
                onClick={startTikTokConnect} disabled={connectingTikTok} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
                data-testid="tiktok-connection-card-connect-button"
              >
                {connectingTikTok ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plug className="h-4 w-4 mr-1" />} Conectar
              </Button>
            )}
          </div>
          <p className="text-[10px] text-[#A1A1AA] mt-3">
            El link de seguimiento se agrega al texto igual que en las demás redes, pero en TikTok no es clicable
            desde la descripción -- para que la gente lo pueda tocar, pegalo una vez en la bio de tu cuenta.
          </p>
        </Card>
      </div>

      <div>
        <p className="text-sm text-[#71717A] mb-3">Publicación de blogs en tu sitio (WordPress).</p>
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="wordpress-connection-card">
          {wpLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#0055FF]" /></div>
          ) : !wpConnection ? (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-[240px]">
                <div className="h-10 w-10 rounded-sm grid place-items-center bg-[#0055FF]/10 shrink-0">
                  <Globe className="h-5 w-5 text-[#0055FF]" />
                </div>
                <div className="flex-1">
                  <div className="font-heading font-bold text-[#09090B]">WordPress</div>
                  <Input
                    value={wpSiteUrl} onChange={(e) => setWpSiteUrl(e.target.value)}
                    placeholder="tusitio.com" className="mt-1 max-w-xs" data-testid="wordpress-site-url-input"
                  />
                </div>
              </div>
              <Button
                onClick={startWordPressConnect} disabled={wpConnecting || !wpSiteUrl.trim()}
                className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" data-testid="wordpress-connect-button"
              >
                {wpConnecting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plug className="h-4 w-4 mr-1" />} Conectar
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-sm grid place-items-center bg-[#0055FF]/10">
                    <Globe className="h-5 w-5 text-[#0055FF]" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-[#09090B]" data-testid="wordpress-connected-site">{wpConnection.site_url}</div>
                    <Badge className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${STATUS[wpConnection.connection_status]?.cls || STATUS.pending.cls}`}>
                      {STATUS[wpConnection.connection_status]?.label || wpConnection.connection_status}
                    </Badge>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-[#EF4444]" onClick={disconnectWordPress} data-testid="wordpress-disconnect-button">
                  <Unplug className="h-3.5 w-3.5 mr-1" /> Desconectar
                </Button>
              </div>
              {wpConnection.connection_status === "disconnected" && wpConnection.disconnect_reason && (
                <div className="flex items-start gap-2 rounded-sm border border-[#EF4444]/30 bg-[#EF4444]/10 p-2 text-xs text-[#7F1D1D]" data-testid="wordpress-disconnect-reason">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {wpConnection.disconnect_reason}
                </div>
              )}
              <div className="text-xs text-[#71717A] space-x-3">
                <span data-testid="wordpress-can-publish">
                  {wpConnection.can_publish ? "Puede publicar directo" : "Solo puede dejar borradores (sin permiso de publicar)"}
                </span>
                <span data-testid="wordpress-can-create-categories">
                  {wpConnection.can_create_categories ? "· Puede crear categorías nuevas" : "· No puede crear categorías nuevas (solo elegir existentes)"}
                </span>
              </div>
            </div>
          )}
        </Card>
      </div>

      <div>
        <p className="text-sm text-[#71717A] mb-3">Búsqueda orgánica (Google Search Console).</p>
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="google-connection-card">
          {googleLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#0055FF]" /></div>
          ) : !googleConn ? (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm grid place-items-center bg-[#0055FF]/10">
                  <Search className="h-5 w-5 text-[#0055FF]" />
                </div>
                <div>
                  <div className="font-heading font-bold text-[#09090B]">Google</div>
                  <div className="text-xs text-[#71717A]">Traé las búsquedas reales que traen visitas a tu sitio.</div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Button data-testid="connect-google-button" onClick={startGoogleConnect} disabled={connectingGoogle} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md">
                  {connectingGoogle ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plug className="h-4 w-4 mr-1" />} Conectar con Google
                </Button>
                {googleConfigError && (
                  <div className="flex items-center gap-1.5 text-xs text-[#EF4444]" data-testid="google-config-error">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {googleConfigError}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-sm grid place-items-center bg-[#0055FF]/10">
                    <Search className="h-5 w-5 text-[#0055FF]" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-[#09090B]" data-testid="google-connected-email">{googleConn.google_email}</div>
                    <Badge className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${STATUS[googleConn.connection_status]?.cls || STATUS.pending.cls}`}>
                      {STATUS[googleConn.connection_status]?.label || googleConn.connection_status}
                    </Badge>
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="text-[#EF4444]" onClick={disconnectGoogle} data-testid="disconnect-google-button">
                  <Unplug className="h-3.5 w-3.5 mr-1" /> Desconectar
                </Button>
              </div>

              {googleConn.connection_status === "connected" && (
                <div className="mt-4">
                  {googleSites.length === 0 ? (
                    <p className="text-xs text-[#71717A]" data-testid="google-no-sites-message">
                      Esta cuenta de Google no tiene ningún sitio verificado en Search Console.
                    </p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#71717A]">Sitio:</span>
                      <Select value={googleConn.selected_site_url || undefined} onValueChange={selectGoogleSite}>
                        <SelectTrigger data-testid="google-site-select" className="h-8 w-64 text-xs">
                          <SelectValue placeholder="Elegí un sitio" />
                        </SelectTrigger>
                        <SelectContent>
                          {googleSites.map((s) => (
                            <SelectItem key={s.siteUrl} value={s.siteUrl}>{s.siteUrl}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {googleConn.last_sync_error && (
                    <p className="text-xs text-[#EF4444] mt-2" data-testid="google-last-sync-error">{googleConn.last_sync_error}</p>
                  )}
                </div>
              )}
              {googleConn.connection_status !== "connected" && (
                <div className="mt-4 flex items-center justify-between flex-wrap gap-3 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-sm p-3">
                  <div className="flex items-center gap-2 text-xs text-[#92400E]" data-testid="google-disconnected-message">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    La conexión con Google venció{googleConn.last_sync_at
                      ? ` (última sincronización: ${formatTenantDateTime(googleConn.last_sync_at, activeTenant?.timezone)})`
                      : ""} -- hay que reconectar para volver a traer datos de Search Console/Analytics.
                  </div>
                  <Button
                    size="sm" onClick={startGoogleConnect} disabled={connectingGoogle}
                    className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" data-testid="reconnect-google-button"
                  >
                    {connectingGoogle ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plug className="h-3.5 w-3.5 mr-1" />} Reconectar
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {isConfigurator && (
        <div>
          <p className="text-sm text-[#71717A] mb-3">
            SMS educativos (Prompt 31). Cada cliente necesita su propia marca y campaña A2P registradas
            en Twilio -- el trámite regulatorio tarda 3-4 semanas y no se puede acelerar desde acá.
          </p>
          <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="twilio-connection-card">
            {twilioLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#0055FF]" /></div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-sm grid place-items-center bg-[#0055FF]/10">
                      <MessageSquare className="h-5 w-5 text-[#0055FF]" />
                    </div>
                    <div>
                      <div className="font-heading font-bold text-[#09090B]">Twilio</div>
                      <div className="text-xs text-[#71717A]" data-testid="twilio-account-sid-masked">
                        {twilioConn ? `Cuenta ${maskSid(twilioConn.account_sid)}` : "Todavía no cargaste credenciales"}
                      </div>
                    </div>
                  </div>
                  {twilioConn && (
                    <Badge
                      className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${
                        twilioConn.registration_status === "approved" ? "bg-[#10B981]/10 text-[#065F46]"
                        : twilioConn.registration_status === "rejected" ? "bg-[#EF4444]/10 text-[#991B1B]"
                        : "bg-[#F59E0B]/10 text-[#92400E]"
                      }`}
                      data-testid="twilio-registration-status"
                    >
                      {twilioConn.registration_status === "approved" ? "Registro aprobado"
                        : twilioConn.registration_status === "rejected" ? "Registro rechazado" : "Registro pendiente"}
                    </Badge>
                  )}
                </div>

                {twilioConn?.rejected_reason && (
                  <p className="text-xs text-[#EF4444]" data-testid="twilio-rejected-reason">{twilioConn.rejected_reason}</p>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Account SID" value={twilioForm.account_sid} data-testid="twilio-account-sid-input"
                    onChange={(e) => setTwilioForm((f) => ({ ...f, account_sid: e.target.value }))} className="text-xs" />
                  <Input placeholder="Messaging Service SID" value={twilioForm.messaging_service_sid} data-testid="twilio-messaging-service-sid-input"
                    onChange={(e) => setTwilioForm((f) => ({ ...f, messaging_service_sid: e.target.value }))} className="text-xs" />
                  <Input placeholder="API Key SID" value={twilioForm.api_key_sid} data-testid="twilio-api-key-sid-input"
                    onChange={(e) => setTwilioForm((f) => ({ ...f, api_key_sid: e.target.value }))} className="text-xs" />
                  <Input type="password" placeholder="API Key Secret" value={twilioForm.api_key_secret} data-testid="twilio-api-key-secret-input"
                    onChange={(e) => setTwilioForm((f) => ({ ...f, api_key_secret: e.target.value }))} className="text-xs" />
                </div>
                <p className="text-[10px] text-[#A1A1AA]">
                  Usá un API Key SID + Secret de Twilio (no el Auth Token maestro) -- se revoca solo, sin afectar el resto de la cuenta.
                </p>

                {twilioConn?.inbound_webhook_url && (
                  <div className="space-y-1">
                    <label className="text-[10px] text-[#71717A] uppercase tracking-wider">
                      URL de webhook entrante (pegar en Twilio: Messaging Service → Inbound Settings)
                    </label>
                    <div className="flex gap-2">
                      <Input
                        readOnly value={twilioConn.inbound_webhook_url} className="text-xs font-mono"
                        data-testid="twilio-inbound-webhook-url"
                        onFocus={(e) => e.target.select()}
                      />
                      <Button
                        size="sm" variant="outline" type="button" data-testid="twilio-copy-webhook-url-button"
                        onClick={() => { navigator.clipboard.writeText(twilioConn.inbound_webhook_url); toast.success("URL copiada"); }}
                      >
                        Copiar
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button size="sm" onClick={saveTwilio} disabled={twilioSaving} className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md" data-testid="twilio-save-button">
                    {twilioSaving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null} Guardar credenciales
                  </Button>
                  {twilioConn && twilioConn.registration_status !== "approved" && (
                    <Button size="sm" variant="outline" onClick={approveTwilio} disabled={twilioApproving} data-testid="twilio-approve-button">
                      {twilioApproving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1" />}
                      Marcar registro A2P como aprobado
                    </Button>
                  )}
                </div>
                <p className="text-[10px] text-[#A1A1AA]">
                  Marcalo como aprobado solo cuando Twilio confirme que la marca y la campaña de este cliente
                  fueron aprobadas -- queda registrado quién y cuándo. Si Twilio rechaza un envío por campaña
                  no aprobada, esto se corta solo.
                </p>
              </div>
            )}
          </Card>
        </div>
      )}

      {isConfigurator && (
        <div>
          <p className="text-sm text-[#71717A] mb-3">
            Google Business Profile (Prompt 32). Misma cuenta de Google que Search Console/Analytics, sin
            OAuth nuevo. No hay entorno de pruebas de Google para esto: toda escritura queda en modo
            simulación hasta que se active a mano.
          </p>
          <Card className="rounded-sm border-[#E4E4E7] shadow-none p-6" data-testid="gbp-connection-card">
            {gbpLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#0055FF]" /></div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-sm grid place-items-center bg-[#0055FF]/10">
                      <Store className="h-5 w-5 text-[#0055FF]" />
                    </div>
                    <div>
                      <div className="font-heading font-bold text-[#09090B]">Google Business Profile</div>
                      <div className="text-xs text-[#71717A]" data-testid="gbp-selected-location">
                        {gbpStatus?.selected_location_title || "Todavía no hay una ficha seleccionada"}
                      </div>
                    </div>
                  </div>
                  <Badge
                    className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${
                      GBP_STATUS_BADGE[gbpStatus?.gbp_access_status]?.cls || GBP_STATUS_BADGE.unavailable.cls
                    }`}
                    data-testid="gbp-access-status-badge"
                  >
                    {GBP_STATUS_BADGE[gbpStatus?.gbp_access_status]?.label || GBP_STATUS_BADGE.unavailable.label}
                  </Badge>
                </div>

                {gbpStatus?.gbp_access_detail && (
                  <p className="text-xs text-[#71717A]" data-testid="gbp-access-detail">{gbpStatus.gbp_access_detail}</p>
                )}

                {gbpStatus?.simulation_mode && (
                  <p className="text-[10px] text-[#F59E0B]" data-testid="gbp-simulation-notice">
                    Modo simulación activo: los posts a la ficha se registran con su payload exacto pero
                    NO se publican de verdad todavía.
                  </p>
                )}

                {gbpStatus?.gbp_access_status === "ready" ? (
                  gbpLoadingLocations ? (
                    <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-[#0055FF]" /></div>
                  ) : gbpLocations.length === 0 ? (
                    <p className="text-xs text-[#71717A]">Esta cuenta de Google no tiene ninguna ficha de Business Profile.</p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#71717A]">Ficha:</span>
                      <Select value={gbpStatus?.selected_location_name || undefined} onValueChange={selectGbpLocation} disabled={gbpSelecting}>
                        <SelectTrigger data-testid="gbp-location-select" className="h-8 w-64 text-xs">
                          <SelectValue placeholder="Elegí una ficha" />
                        </SelectTrigger>
                        <SelectContent>
                          {gbpLocations.map((l) => (
                            <SelectItem key={l.location_name} value={l.location_name}>{l.location_title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )
                ) : (
                  <Button
                    size="sm" variant="outline" onClick={checkGbpAccess} disabled={gbpChecking}
                    data-testid="gbp-check-access-button"
                  >
                    {gbpChecking ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                    Verificar acceso
                  </Button>
                )}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
